import React, { useState, useEffect, useRef } from 'react';
import { Mail, Home, MapPin, Play, Pause, RotateCcw, Navigation, Truck, Clock, Zap, Activity, Users, Target } from 'lucide-react';
import FleetCommandHeader from './FleetCommandHeader';
import './SanDiegoMailDeliveryMap.css';

const SanDiegoMailDeliveryMap = () => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [deliveredHomes, setDeliveredHomes] = useState(new Set());
  const [currentMailPosition, setCurrentMailPosition] = useState(null);
  const [currentRouteIndex, setCurrentRouteIndex] = useState(0);
  const [deliveryHistory, setDeliveryHistory] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [mapMode, setMapMode] = useState('satellite'); // satellite, street, hybrid
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [truckStats, setTruckStats] = useState({
    speed: 0,
    efficiency: 98.5,
    fuelLevel: 87,
    packagesLeft: 0
  });
  
  const animationRef = useRef();
  const startTimeRef = useRef(null);

  // Enhanced San Diego data with real postal codes and demographics
  const neighborhoods = [
    { 
      id: 1, name: "La Jolla", lng: -117.2713, lat: 32.8328, homes: 18, 
      color: [59, 130, 246], zipCode: "92037", avgIncome: "$120k", density: "Low",
      demographics: { residential: 85, business: 15 }
    },
    { 
      id: 2, name: "Pacific Beach", lng: -117.2531, lat: 32.7940, homes: 22, 
      color: [16, 185, 129], zipCode: "92109", avgIncome: "$68k", density: "High",
      demographics: { residential: 70, business: 30 }
    },
    { 
      id: 3, name: "Mission Beach", lng: -117.2527, lat: 32.7731, homes: 12, 
      color: [245, 158, 11], zipCode: "92109", avgIncome: "$72k", density: "Medium",
      demographics: { residential: 60, business: 40 }
    },
    { 
      id: 4, name: "Downtown", lng: -117.1611, lat: 32.7157, homes: 35, 
      color: [239, 68, 68], zipCode: "92101", avgIncome: "$85k", density: "Very High",
      demographics: { residential: 40, business: 60 }
    },
    { 
      id: 5, name: "Balboa Park", lng: -117.1442, lat: 32.7341, homes: 15, 
      color: [139, 92, 246], zipCode: "92103", avgIncome: "$76k", density: "Medium",
      demographics: { residential: 80, business: 20 }
    },
    { 
      id: 6, name: "Hillcrest", lng: -117.1661, lat: 32.7486, homes: 24, 
      color: [236, 72, 153], zipCode: "92103", avgIncome: "$82k", density: "High",
      demographics: { residential: 75, business: 25 }
    },
    { 
      id: 7, name: "Point Loma", lng: -117.2443, lat: 32.7081, homes: 19, 
      color: [6, 182, 212], zipCode: "92107", avgIncome: "$95k", density: "Medium",
      demographics: { residential: 90, business: 10 }
    },
    { 
      id: 8, name: "Coronado", lng: -117.1831, lat: 32.6859, homes: 14, 
      color: [132, 204, 22], zipCode: "92118", avgIncome: "$135k", density: "Low",
      demographics: { residential: 95, business: 5 }
    },
    { 
      id: 9, name: "Chula Vista", lng: -117.0842, lat: 32.6401, homes: 28, 
      color: [249, 115, 22], zipCode: "91910", avgIncome: "$75k", density: "High",
      demographics: { residential: 85, business: 15 }
    },
    { 
      id: 10, name: "Clairemont", lng: -117.2003, lat: 32.7714, homes: 21, 
      color: [168, 85, 247], zipCode: "92111", avgIncome: "$69k", density: "Medium",
      demographics: { residential: 88, business: 12 }
    }
  ];

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Enhanced home generation with more realistic data
  const generateHomes = () => {
    let homes = [];
    let homeId = 1;
    
    neighborhoods.forEach(neighborhood => {
      const businessCount = Math.floor(neighborhood.homes * (neighborhood.demographics.business / 100));
      const residentialCount = neighborhood.homes - businessCount;
      
      // Generate business addresses
      for (let i = 0; i < businessCount; i++) {
        const lngOffset = (Math.random() - 0.5) * 0.012;
        const latOffset = (Math.random() - 0.5) * 0.012;
        
        homes.push({
          id: homeId++,
          neighborhoodId: neighborhood.id,
          lng: neighborhood.lng + lngOffset,
          lat: neighborhood.lat + latOffset,
          delivered: false,
          type: 'business',
          priority: Math.random() > 0.7 ? 'express' : 'standard',
          packages: Math.floor(Math.random() * 5) + 1,
          address: `${Math.floor(Math.random() * 9999) + 1} ${neighborhood.name} Business Plaza`,
          zipCode: neighborhood.zipCode,
          deliveryTime: null,
          recipient: `${['ABC Corp', 'XYZ LLC', 'Main Street Shop', 'Local Business'][Math.floor(Math.random() * 4)]}`
        });
      }
      
      // Generate residential addresses
      for (let i = 0; i < residentialCount; i++) {
        const lngOffset = (Math.random() - 0.5) * 0.015;
        const latOffset = (Math.random() - 0.5) * 0.015;
        
        homes.push({
          id: homeId++,
          neighborhoodId: neighborhood.id,
          lng: neighborhood.lng + lngOffset,
          lat: neighborhood.lat + latOffset,
          delivered: false,
          type: Math.random() > 0.6 ? 'apartment' : 'house',
          priority: Math.random() > 0.9 ? 'express' : 'standard',
          packages: Math.floor(Math.random() * 3) + 1,
          address: `${Math.floor(Math.random() * 9999) + 1} ${neighborhood.name} ${Math.random() > 0.5 ? 'Ave' : 'St'}`,
          zipCode: neighborhood.zipCode,
          deliveryTime: null,
          recipient: `${['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller'][Math.floor(Math.random() * 7)]} Family`
        });
      }
    });
    
    return homes;
  };

  const [homes] = useState(generateHomes());

  // Enhanced mail center
  const mailCenter = { 
    lng: -117.1394, 
    lat: 32.7069, 
    name: "San Diego Processing & Distribution Center",
    address: "11251 Rancho Carmel Dr, San Diego, CA 92199",
    capacity: "2.5M packages/day",
    zone: "SCF 921",
    employees: 1250
  };

  // Smart route optimization
  const createRoutes = () => {
    const routes = [];
    
    // Prioritize express deliveries first
    const expressHomes = homes.filter(h => h.priority === 'express');
    const standardHomes = homes.filter(h => h.priority === 'standard');
    
    const processHomes = (homeList, routeType) => {
      const homesByNeighborhood = {};
      homeList.forEach(home => {
        if (!homesByNeighborhood[home.neighborhoodId]) {
          homesByNeighborhood[home.neighborhoodId] = [];
        }
        homesByNeighborhood[home.neighborhoodId].push(home);
      });

      Object.values(homesByNeighborhood).forEach(neighborhoodHomes => {
        // Sort by optimal delivery order (businesses first in morning, residential later)
        const sortedHomes = neighborhoodHomes.sort((a, b) => {
          if (a.type === 'business' && b.type !== 'business') return -1;
          if (a.type !== 'business' && b.type === 'business') return 1;
          
          const distA = Math.sqrt(Math.pow(a.lng - mailCenter.lng, 2) + Math.pow(a.lat - mailCenter.lat, 2));
          const distB = Math.sqrt(Math.pow(b.lng - mailCenter.lng, 2) + Math.pow(b.lat - mailCenter.lat, 2));
          return distA - distB;
        });

        for (let i = 0; i < sortedHomes.length; i += 12) {
          const routeHomes = sortedHomes.slice(i, i + 12);
          routes.push({
            homes: routeHomes,
            type: routeType,
            estimatedTime: routeHomes.length * 2.5 + 15, // minutes
            distance: calculateRouteDistance(routeHomes),
            priority: routeType === 'express' ? 1 : 2
          });
        }
      });
    };

    processHomes(expressHomes, 'express');
    processHomes(standardHomes, 'standard');
    
    return routes.sort((a, b) => a.priority - b.priority);
  };

  const calculateRouteDistance = (routeHomes) => {
    let distance = 0;
    let prevPoint = mailCenter;
    
    routeHomes.forEach(home => {
      distance += Math.sqrt(Math.pow(home.lng - prevPoint.lng, 2) + Math.pow(home.lat - prevPoint.lat, 2));
      prevPoint = home;
    });
    
    return (distance * 69).toFixed(1); // Convert to approximate miles
  };

  const [routes] = useState(createRoutes());

  // Update truck stats during animation
  const updateTruckStats = (position, isMoving) => {
    if (isMoving) {
      setTruckStats(prev => ({
        ...prev,
        speed: Math.random() * 15 + 25, // 25-40 mph
        efficiency: Math.max(95, prev.efficiency + (Math.random() - 0.5) * 0.5),
        fuelLevel: Math.max(0, prev.fuelLevel - 0.1),
        packagesLeft: routes.length > 0 ? routes[currentRouteIndex]?.homes?.length - deliveredHomes.size || 0 : 0
      }));
    } else {
      setTruckStats(prev => ({ ...prev, speed: 0 }));
    }
  };

  // Enhanced animation with realistic timing
  const animateMailDelivery = async () => {
    if (currentRouteIndex >= routes.length) return;
    if (!startTimeRef.current) startTimeRef.current = Date.now();

    const currentRoute = routes[currentRouteIndex];
    let startPoint = mailCenter;

    for (let i = 0; i < currentRoute.homes.length; i++) {
      const targetHome = currentRoute.homes[i];
      
      const distance = Math.sqrt(
        Math.pow(targetHome.lng - startPoint.lng, 2) + 
        Math.pow(targetHome.lat - startPoint.lat, 2)
      );
      
      // Realistic timing based on distance and traffic
      const baseTime = distance * 45000;
      const trafficFactor = 1 + (Math.random() * 0.4); // Traffic variation
      const travelTime = Math.max(800, baseTime * trafficFactor);
      
      updateTruckStats(null, true);
      await animateMovement(startPoint, targetHome, travelTime);
      
      // Delivery process
      const deliveryTime = targetHome.type === 'business' ? 300 : 
                          targetHome.type === 'apartment' ? 600 : 400;
      
      setDeliveredHomes(prev => new Set([...prev, targetHome.id]));
      
      // Add to delivery history
      const deliveryRecord = {
        ...targetHome,
        deliveryTime: new Date(),
        duration: deliveryTime,
        routeIndex: currentRouteIndex + 1
      };
      setDeliveryHistory(prev => [deliveryRecord, ...prev].slice(0, 50));
      
      updateTruckStats(null, false);
      await new Promise(resolve => setTimeout(resolve, deliveryTime));
      
      startPoint = targetHome;
    }

    // Return to processing center
    updateTruckStats(null, true);
    await animateMovement(startPoint, mailCenter, 1800);
    setCurrentMailPosition(null);
    updateTruckStats(null, false);
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    setCurrentRouteIndex(prev => prev + 1);
  };

  const animateMovement = (start, end, duration) => {
    return new Promise(resolve => {
      const startTime = Date.now();
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        const easeProgress = 1 - Math.pow(1 - progress, 2.5);
        
        const currentLng = start.lng + (end.lng - start.lng) * easeProgress;
        const currentLat = start.lat + (end.lat - start.lat) * easeProgress;
        
        setCurrentMailPosition({ lng: currentLng, lat: currentLat });
        
        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };
      
      animate();
    });
  };

  useEffect(() => {
    if (isAnimating && currentRouteIndex < routes.length) {
      animateMailDelivery();
    } else if (currentRouteIndex >= routes.length && isAnimating) {
      setIsAnimating(false);
    }
  }, [isAnimating, currentRouteIndex, routes.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const startAnimation = () => {
    setIsAnimating(true);
    if (!startTimeRef.current) startTimeRef.current = Date.now();
  };

  const pauseAnimation = () => {
    setIsAnimating(false);
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  };

  const resetAnimation = () => {
    pauseAnimation();
    setDeliveredHomes(new Set());
    setCurrentMailPosition(null);
    setCurrentRouteIndex(0);
    setDeliveryHistory([]);
    setTruckStats({ speed: 0, efficiency: 98.5, fuelLevel: 87, packagesLeft: 0 });
    startTimeRef.current = null;
    setIsAnimating(false);
  };

  // Convert coordinates to screen positions
  const projectToScreen = (lng, lat) => {
    const x = ((lng - (-117.3)) / 0.25) * 100;
    const y = (1 - (lat - 32.6) / 0.25) * 100;
    return { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
  };

  const totalPackages = homes.reduce((sum, home) => sum + home.packages, 0);
  const deliveredPackages = homes.filter(h => deliveredHomes.has(h.id)).reduce((sum, home) => sum + home.packages, 0);
  const efficiency = deliveredHomes.size > 0 ? ((deliveredHomes.size / (currentTime - (startTimeRef.current || currentTime)) * 60000) * 60).toFixed(1) : 0;

  return (
    <div className="sdm-container">
      {/* Animated background */}
      <div className="sdm-background">
        <div className="sdm-background-gradient" />
      </div>

      <div className="sdm-inner">
        {/* Enhanced Fleet Command Header */}
        <FleetCommandHeader
          isAnimating={isAnimating}
          setIsAnimating={setIsAnimating}
          currentTime={currentTime}
          mapMode={mapMode}
          setMapMode={setMapMode}
          showHeatmap={showHeatmap}
          setShowHeatmap={setShowHeatmap}
          onStartAnimation={startAnimation}
          onPauseAnimation={pauseAnimation}
          onResetAnimation={resetAnimation}
        />

        {/* Enhanced Metrics Dashboard */}
        <div className="sdm-stats">
          <div className="sdm-card">
            <div className="sdm-card-top">
              <div>
                <div className="sdm-metric sdm-metric-blue">{homes.length}</div>
                <div className="sdm-label">Delivery Points</div>
              </div>
              <div className="sdm-card-icon sdm-card-blue">
                <Home className="sdm-icon" />
              </div>
            </div>
          </div>

          <div className="sdm-card">
            <div className="sdm-card-top">
              <div>
                <div className="sdm-metric sdm-metric-green">{deliveredHomes.size}</div>
                <div className="sdm-label">Completed</div>
              </div>
              <div className="sdm-card-icon sdm-card-green">
                <Mail className="sdm-icon" />
              </div>
            </div>
          </div>

          <div className="sdm-card">
            <div className="sdm-card-top">
              <div>
                <div className="sdm-metric sdm-metric-purple">{totalPackages}</div>
                <div className="sdm-label">Total Packages</div>
              </div>
              <div className="sdm-card-icon sdm-card-purple">
                <Target className="sdm-icon" />
              </div>
            </div>
          </div>

          <div className="sdm-card">
            <div className="sdm-card-top">
              <div>
                <div className="sdm-metric sdm-metric-cyan">{deliveredPackages}</div>
                <div className="sdm-label">Delivered Packages</div>
              </div>
              <div className="sdm-card-icon sdm-card-cyan">
                <Zap className="sdm-icon" />
              </div>
            </div>
          </div>

          <div className="sdm-card">
            <div className="sdm-card-top">
              <div>
                <div className="sdm-metric sdm-metric-orange">{routes.length}</div>
                <div className="sdm-label">Active Routes</div>
              </div>
              <div className="sdm-card-icon sdm-card-orange">
                <Navigation className="sdm-icon" />
              </div>
            </div>
          </div>

          <div className="sdm-card">
            <div className="sdm-card-top">
              <div>
                <div className="sdm-metric sdm-metric-pink">
                  {Math.round((deliveredHomes.size / homes.length) * 100)}%
                </div>
                <div className="sdm-label">Progress</div>
              </div>
              <div className="sdm-card-icon sdm-card-pink">
                <Activity className="sdm-icon" />
              </div>
            </div>
          </div>

          <div className="sdm-card">
            <div className="sdm-card-top">
              <div>
                <div className="sdm-metric sdm-metric-emerald">{efficiency}</div>
                <div className="sdm-label">Efficiency/hr</div>
              </div>
              <div className="sdm-card-icon sdm-card-emerald">
                <Users className="sdm-icon" />
              </div>
            </div>
          </div>
        </div>

        {/* Main Map and Side Panel Layout */}
        <div className="sdm-content">
          {/* Enhanced 3D Map */}
          <div className="sdm-map">
            <div className={`sdm-map-background ${mapMode === 'satellite' ? 'satellite' : 'street'}`} />
            <div className="sdm-street-pattern" />

            <svg width="100%" height="100%" viewBox="0 0 100 100" className="sdm-svg">
              <defs>
                {/* Advanced gradients and filters */}
                <radialGradient id="heatmapGradient" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="rgba(239, 68, 68, 0.6)" />
                  <stop offset="50%" stopColor="rgba(245, 158, 11, 0.4)" />
                  <stop offset="100%" stopColor="rgba(16, 185, 129, 0.2)" />
                </radialGradient>
                
                <filter id="neonGlow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>

                <filter id="truckGlow" x="-100%" y="-100%" width="300%" height="300%">
                  <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
                  <feDropShadow dx="0.5" dy="0.5" stdDeviation="1" floodColor="#000000" floodOpacity="0.8"/>
                  <feMerge>
                    <feMergeNode in="SourceGraphic"/>
                    <feMergeNode in="coloredBlur"/>
                  </feMerge>
                </filter>

                {/* Dynamic route pattern */}
                <pattern id="routePattern" patternUnits="userSpaceOnUse" width="4" height="4">
                  <rect width="4" height="4" fill="transparent"/>
                  <circle cx="2" cy="2" r="0.8" fill="rgba(96, 165, 250, 0.6)"/>
                </pattern>
              </defs>

              {/* Geographic features */}
              <path
                d="M 0 65 Q 25 55 35 50 Q 45 48 55 52 Q 65 58 75 64 Q 85 70 95 76 L 100 82 L 100 100 L 0 100 Z"
                fill={mapMode === 'satellite' ? "rgba(30, 64, 175, 0.2)" : "rgba(59, 130, 246, 0.15)"}
                stroke="rgba(59, 130, 246, 0.4)"
                strokeWidth="0.3"
              />

              {/* Highway network */}
              <g stroke="rgba(156, 163, 175, 0.8)" strokeWidth="0.6" fill="none">
                <path d="M 15 25 Q 35 20 55 25 Q 75 30 95 25" strokeDasharray="3,1.5" />
                <path d="M 10 45 Q 30 40 50 45 Q 70 50 90 45" strokeDasharray="3,1.5" />
                <path d="M 20 75 Q 40 70 60 75 Q 80 80 100 75" strokeDasharray="3,1.5" />
                <path d="M 25 15 L 30 85" />
                <path d="M 50 10 L 55 90" />
                <path d="M 75 15 L 80 85" />
              </g>

              {/* Heatmap overlay */}
              {showHeatmap && neighborhoods.map(neighborhood => {
                const pos = projectToScreen(neighborhood.lng, neighborhood.lat);
                const intensity = neighborhood.homes / 35;
                return (
                  <circle
                    key={`heatmap-${neighborhood.id}`}
                    cx={pos.x}
                    cy={pos.y}
                    r={8 + intensity * 15}
                    fill="url(#heatmapGradient)"
                    opacity={0.6}
                    className="sdm-heatmap-circle"
                  />
                );
              })}

              {/* Enhanced neighborhood visualization */}
              {neighborhoods.map(neighborhood => {
                const pos = projectToScreen(neighborhood.lng, neighborhood.lat);
                const deliveredInNeighborhood = homes.filter(h => 
                  h.neighborhoodId === neighborhood.id && deliveredHomes.has(h.id)
                ).length;
                const completionRate = deliveredInNeighborhood / neighborhood.homes;
                
                return (
                  <g key={neighborhood.id}>
                    <circle
                      cx={pos.x}
                      cy={pos.y}
                      r="10"
                      fill="none"
                      stroke={`rgba(${neighborhood.color.join(',')}, ${0.3 + completionRate * 0.5})`}
                      strokeWidth="0.4"
                      strokeDasharray="2,1"
                    />
                    <text
                      x={pos.x}
                      y={pos.y - 12}
                      fontSize="2.2"
                      textAnchor="middle"
                      fill={`rgb(${neighborhood.color.map(c => Math.min(255, c + 80)).join(',')})`}
                      className="sdm-neighborhood-name"
                    >
                      {neighborhood.name}
                    </text>
                    <text
                      x={pos.x}
                      y={pos.y - 9}
                      fontSize="1.4"
                      textAnchor="middle"
                      fill={`rgba(${neighborhood.color.join(',')}, 0.8)`}
                      className="sdm-neighborhood-progress"
                    >
                      {Math.round(completionRate * 100)}%
                    </text>
                  </g>
                );
              })}

              {/* Processing Center */}
              <g>
                {(() => {
                  const centerPos = projectToScreen(mailCenter.lng, mailCenter.lat);
                  return (
                    <g filter="url(#truckGlow)">
                      <rect
                        x={centerPos.x - 5}
                        y={centerPos.y - 3.5}
                        width="10"
                        height="7"
                        fill="#dc2626"
                        stroke="#b91c1c"
                        strokeWidth="0.3"
                        rx="0.8"
                      />
                      <rect
                        x={centerPos.x - 4}
                        y={centerPos.y - 2.5}
                        width="8"
                        height="5"
                        fill="#ef4444"
                        rx="0.5"
                      />
                      <text
                        x={centerPos.x}
                        y={centerPos.y - 5}
                        fontSize="2"
                        textAnchor="middle"
                        fill="#fecaca"
                        className="sdm-center-label"
                      >
                        🏢 USPS PDC
                      </text>
                      <text
                        x={centerPos.x}
                        y={centerPos.y + 5.5}
                        fontSize="1.2"
                        textAnchor="middle"
                        fill="#fca5a5"
                        className="sdm-center-capacity"
                      >
                        {mailCenter.capacity}
                      </text>
                    </g>
                  );
                })()}
              </g>

              {/* Enhanced delivery locations */}
              {homes.map(home => {
                const pos = projectToScreen(home.lng, home.lat);
                const isDelivered = deliveredHomes.has(home.id);
                const isExpress = home.priority === 'express';
                
                if (home.type === 'business') {
                  return (
                    <g key={home.id} filter={isDelivered ? "url(#neonGlow)" : "none"}>
                      <rect
                        x={pos.x - 1.2}
                        y={pos.y - 2}
                        width="2.4"
                        height="4"
                        fill={isDelivered ? "#10b981" : (isExpress ? "#f59e0b" : "#ef4444")}
                        stroke={isDelivered ? "#059669" : (isExpress ? "#d97706" : "#dc2626")}
                        strokeWidth="0.2"
                        rx="0.3"
                        className="sdm-building"
                      />
                      {/* Building details */}
                      <rect x={pos.x - 0.8} y={pos.y - 1.5} width="0.4" height="0.6" fill={isDelivered ? "#6ee7b7" : "#fca5a5"} rx="0.1" />
                      <rect x={pos.x + 0.4} y={pos.y - 1.5} width="0.4" height="0.6" fill={isDelivered ? "#6ee7b7" : "#fca5a5"} rx="0.1" />
                      <rect x={pos.x - 0.8} y={pos.y - 0.5} width="0.4" height="0.6" fill={isDelivered ? "#6ee7b7" : "#fca5a5"} rx="0.1" />
                      <rect x={pos.x + 0.4} y={pos.y - 0.5} width="0.4" height="0.6" fill={isDelivered ? "#6ee7b7" : "#fca5a5"} rx="0.1" />
                      <rect x={pos.x - 0.8} y={pos.y + 0.5} width="0.4" height="0.6" fill={isDelivered ? "#6ee7b7" : "#fca5a5"} rx="0.1" />
                      <rect x={pos.x + 0.4} y={pos.y + 0.5} width="0.4" height="0.6" fill={isDelivered ? "#6ee7b7" : "#fca5a5"} rx="0.1" />
                      {/* Package count indicator */}
                      <circle cx={pos.x + 1} cy={pos.y - 1.5} r="0.5" fill="#1f2937" stroke={isExpress ? "#f59e0b" : "#6b7280"} strokeWidth="0.1" />
                      <text x={pos.x + 1} y={pos.y - 1.3} fontSize="0.8" textAnchor="middle" fill="white" className="sdm-package-count">
                        {home.packages}
                      </text>
                    </g>
                  );
                } else if (home.type === 'apartment') {
                  return (
                    <g key={home.id} filter={isDelivered ? "url(#neonGlow)" : "none"}>
                      <rect
                        x={pos.x - 1}
                        y={pos.y - 1.8}
                        width="2"
                        height="3.6"
                        fill={isDelivered ? "#10b981" : (isExpress ? "#f59e0b" : "#ef4444")}
                        stroke={isDelivered ? "#059669" : (isExpress ? "#d97706" : "#dc2626")}
                        strokeWidth="0.2"
                        rx="0.2"
                        className="sdm-apartment"
                      />
                      {/* Apartment windows */}
                      <rect x={pos.x - 0.7} y={pos.y - 1.3} width="0.5" height="0.4" fill={isDelivered ? "#6ee7b7" : "#fca5a5"} rx="0.1" />
                      <rect x={pos.x + 0.2} y={pos.y - 1.3} width="0.5" height="0.4" fill={isDelivered ? "#6ee7b7" : "#fca5a5"} rx="0.1" />
                      <rect x={pos.x - 0.7} y={pos.y - 0.5} width="0.5" height="0.4" fill={isDelivered ? "#6ee7b7" : "#fca5a5"} rx="0.1" />
                      <rect x={pos.x + 0.2} y={pos.y - 0.5} width="0.5" height="0.4" fill={isDelivered ? "#6ee7b7" : "#fca5a5"} rx="0.1" />
                      <rect x={pos.x - 0.7} y={pos.y + 0.3} width="0.5" height="0.4" fill={isDelivered ? "#6ee7b7" : "#fca5a5"} rx="0.1" />
                      <rect x={pos.x + 0.2} y={pos.y + 0.3} width="0.5" height="0.4" fill={isDelivered ? "#6ee7b7" : "#fca5a5"} rx="0.1" />
                      {/* Package indicator */}
                      <circle cx={pos.x + 0.8} cy={pos.y - 1.3} r="0.4" fill="#1f2937" stroke={isExpress ? "#f59e0b" : "#6b7280"} strokeWidth="0.1" />
                      <text x={pos.x + 0.8} y={pos.y - 1.1} fontSize="0.7" textAnchor="middle" fill="white" className="sdm-package-count">
                        {home.packages}
                      </text>
                    </g>
                  );
                } else {
                  return (
                    <g key={home.id} filter={isDelivered ? "url(#neonGlow)" : "none"}>
                      {/* House roof */}
                      <polygon
                        points={`${pos.x},${pos.y - 2} ${pos.x - 1.3},${pos.y + 0.2} ${pos.x + 1.3},${pos.y + 0.2}`}
                        fill={isDelivered ? "#16a34a" : (isExpress ? "#d97706" : "#dc2626")}
                        stroke={isDelivered ? "#15803d" : (isExpress ? "#b45309" : "#b91c1c")}
                        strokeWidth="0.15"
                        className="sdm-house-roof"
                      />
                      {/* House base */}
                      <rect
                        x={pos.x - 1}
                        y={pos.y - 0.2}
                        width="2"
                        height="1.8"
                        fill={isDelivered ? "#22c55e" : (isExpress ? "#f59e0b" : "#ef4444")}
                        stroke={isDelivered ? "#16a34a" : (isExpress ? "#d97706" : "#dc2626")}
                        strokeWidth="0.15"
                        rx="0.1"
                        className="sdm-house"
                      />
                      {/* Door */}
                      <rect x={pos.x - 0.2} y={pos.y + 0.6} width="0.4" height="1" fill={isDelivered ? "#065f46" : "#7f1d1d"} rx="0.05" />
                      {/* Windows */}
                      <rect x={pos.x - 0.7} y={pos.y + 0.2} width="0.4" height="0.4" fill={isDelivered ? "#a7f3d0" : "#fecaca"} rx="0.05" />
                      <rect x={pos.x + 0.3} y={pos.y + 0.2} width="0.4" height="0.4" fill={isDelivered ? "#a7f3d0" : "#fecaca"} rx="0.05" />
                      {/* Package indicator */}
                      <circle cx={pos.x + 1} cy={pos.y - 1.5} r="0.4" fill="#1f2937" stroke={isExpress ? "#f59e0b" : "#6b7280"} strokeWidth="0.1" />
                      <text x={pos.x + 1} y={pos.y - 1.3} fontSize="0.7" textAnchor="middle" fill="white" className="sdm-package-count">
                        {home.packages}
                      </text>
                    </g>
                  );
                }
              })}

              {/* Advanced mail truck with realistic details */}
              {currentMailPosition && (() => {
                const truckPos = projectToScreen(currentMailPosition.lng, currentMailPosition.lat);
                return (
                  <g transform={`translate(${truckPos.x}, ${truckPos.y})`} filter="url(#truckGlow)">
                    {/* Truck shadow */}
                    <ellipse cx="0.8" cy="4" rx="3.5" ry="1.2" fill="rgba(0,0,0,0.4)" />
                    
                    {/* Main truck body */}
                    <rect x="-2.5" y="-3" width="5" height="6" fill="#1d4ed8" stroke="#1e40af" strokeWidth="0.25" rx="0.5" />
                    
                    {/* Truck cab */}
                    <rect x="-2" y="-3.8" width="4" height="1.5" fill="#3b82f6" stroke="#2563eb" strokeWidth="0.2" rx="0.4" />
                    
                    {/* Windshield */}
                    <rect x="-1.6" y="-3.5" width="3.2" height="1" fill="#7dd3fc" rx="0.3" opacity="0.9" />
                    
                    {/* Side windows */}
                    <rect x="-2.3" y="-2.5" width="0.8" height="0.8" fill="#7dd3fc" rx="0.1" opacity="0.7" />
                    <rect x="1.5" y="-2.5" width="0.8" height="0.8" fill="#7dd3fc" rx="0.1" opacity="0.7" />
                    
                    {/* Wheels with realistic styling */}
                    <circle cx="-1.5" cy="2.5" r="0.8" fill="#374151" stroke="#1f2937" strokeWidth="0.2" />
                    <circle cx="1.5" cy="2.5" r="0.8" fill="#374151" stroke="#1f2937" strokeWidth="0.2" />
                    <circle cx="-1.5" cy="-1" r="0.8" fill="#374151" stroke="#1f2937" strokeWidth="0.2" />
                    <circle cx="1.5" cy="-1" r="0.8" fill="#374151" stroke="#1f2937" strokeWidth="0.2" />
                    
                    {/* Wheel rims */}
                    <circle cx="-1.5" cy="2.5" r="0.4" fill="#6b7280" stroke="#9ca3af" strokeWidth="0.1" />
                    <circle cx="1.5" cy="2.5" r="0.4" fill="#6b7280" stroke="#9ca3af" strokeWidth="0.1" />
                    <circle cx="-1.5" cy="-1" r="0.4" fill="#6b7280" stroke="#9ca3af" strokeWidth="0.1" />
                    <circle cx="1.5" cy="-1" r="0.4" fill="#6b7280" stroke="#9ca3af" strokeWidth="0.1" />
                    
                    {/* USPS branding and details */}
                    <text x="0" y="0.5" fontSize="1.2" textAnchor="middle" fill="white" className="sdm-truck-label">USPS</text>
                    <text x="0" y="1.8" fontSize="0.8" textAnchor="middle" fill="#93c5fd" className="sdm-truck-priority">PRIORITY</text>
                    
                    {/* Speed indicator */}
                    <g opacity="0.8">
                      <path d="M -4 0.5 Q -3.2 0.2 -2.8 0.5 Q -3.2 0.8 -4 0.5" fill="#60a5fa" />
                      <path d="M -4.5 1.5 Q -3.7 1.2 -3.3 1.5 Q -3.7 1.8 -4.5 1.5" fill="#60a5fa" />
                      <path d="M -4.5 -0.5 Q -3.7 -0.8 -3.3 -0.5 Q -3.7 -0.2 -4.5 -0.5" fill="#60a5fa" />
                    </g>
                    
                    {/* GPS antenna */}
                    <rect x="-0.2" y="-4.2" width="0.4" height="0.6" fill="#ef4444" rx="0.1" />
                    <circle cx="0" cy="-4.5" r="0.3" fill="none" stroke="#ef4444" strokeWidth="0.1" opacity="0.6" />
                  </g>
                );
              })()}

              {/* Enhanced route visualization */}
              {isAnimating && currentRouteIndex < routes.length && (
                <g>
                  {routes[currentRouteIndex].homes.reduce((acc, home, index) => {
                    const prevPoint = index === 0 ? mailCenter : routes[currentRouteIndex].homes[index - 1];
                    const startPos = projectToScreen(prevPoint.lng, prevPoint.lat);
                    const endPos = projectToScreen(home.lng, home.lat);
                    
                    acc.push(
                      <g key={`route-${index}`}>
                        <path
                          d={`M ${startPos.x} ${startPos.y} Q ${(startPos.x + endPos.x) / 2 + (Math.random() - 0.5) * 4} ${(startPos.y + endPos.y) / 2 - 4} ${endPos.x} ${endPos.y}`}
                          fill="none"
                          stroke="url(#routePattern)"
                          strokeWidth="0.6"
                          strokeDasharray="2,1"
                          opacity="0.9"
                          filter="url(#neonGlow)"
                        />
                        {/* Route waypoints */}
                        <circle
                          cx={endPos.x}
                          cy={endPos.y}
                          r="1.5"
                          fill="none"
                          stroke={deliveredHomes.has(home.id) ? "#10b981" : "#60a5fa"}
                          strokeWidth="0.3"
                          strokeDasharray="0.5,0.5"
                          className={deliveredHomes.has(home.id) ? "sdm-waypoint delivered" : "sdm-waypoint"}
                        />
                      </g>
                    );
                    return acc;
                  }, [])}
                </g>
              )}
            </svg>

            {/* Map Controls */}
            <div className="sdm-map-controls">
              <div className="sdm-control-group">
                <button className="sdm-control-btn">🔍 Zoom In</button>
                <button className="sdm-control-btn">🔍 Zoom Out</button>
                <button className="sdm-control-btn">📍 Center</button>
              </div>
            </div>

            {/* Live Status Overlays */}
            {isAnimating && (
              <div className="sdm-live-overlays">
                <div className="sdm-live-status active">
                  <div className="sdm-live-dot"></div>
                  <div>
                    <div className="sdm-live-title">FLEET ACTIVE</div>
                    <div className="sdm-live-subtitle">Vehicle ID: SD-{(currentRouteIndex + 1).toString().padStart(3, '0')}</div>
                  </div>
                </div>
                
                {truckStats.speed > 0 && (
                  <div className="sdm-telemetry">
                    <div className="sdm-telemetry-title">TELEMETRY</div>
                    <div className="sdm-telemetry-data">
                      <div className="sdm-telemetry-row">
                        <span>Speed:</span>
                        <span className="sdm-telemetry-value">{truckStats.speed.toFixed(1)} mph</span>
                      </div>
                      <div className="sdm-telemetry-row">
                        <span>Efficiency:</span>
                        <span className="sdm-telemetry-value">{truckStats.efficiency.toFixed(1)}%</span>
                      </div>
                      <div className="sdm-telemetry-row">
                        <span>Fuel:</span>
                        <span className="sdm-telemetry-value">{truckStats.fuelLevel.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Enhanced Side Panel */}
          <div className="sdm-side-panel">
            {/* Live Activity Feed */}
            <div className="sdm-activity-feed">
              <h3 className="sdm-panel-title">
                <Activity className="sdm-panel-icon" />
                Live Activity Feed
              </h3>
              <div className="sdm-activity-list">
                {deliveryHistory.slice(0, 10).map((delivery, idx) => (
                  <div key={delivery.id} className="sdm-activity-item">
                    <div className="sdm-activity-status">
                      ✓ {delivery.type === 'business' ? '🏢' : delivery.type === 'apartment' ? '🏠' : '🏡'} DELIVERED
                    </div>
                    <div className="sdm-activity-address">
                      {delivery.address}
                    </div>
                    <div className="sdm-activity-meta">
                      {delivery.packages} pkg • Route {delivery.routeIndex} • {delivery.deliveryTime.toLocaleTimeString()}
                    </div>
                  </div>
                ))}
                {deliveryHistory.length === 0 && (
                  <div className="sdm-activity-empty">
                    No deliveries yet. Start fleet to begin tracking.
                  </div>
                )}
              </div>
            </div>

            {/* Route Progress */}
            <div className="sdm-route-progress">
              <h3 className="sdm-panel-title">
                <Navigation className="sdm-panel-icon" />
                Route Progress
              </h3>
              {currentRouteIndex < routes.length ? (
                <div className="sdm-route-details">
                  <div className="sdm-route-row">
                    <span>Current Route:</span>
                    <span className="sdm-route-value">
                      {currentRouteIndex + 1} / {routes.length}
                    </span>
                  </div>
                  <div className="sdm-route-row">
                    <span>Type:</span>
                    <span className={`sdm-route-type ${routes[currentRouteIndex].type}`}>
                      {routes[currentRouteIndex].type === 'express' ? '⚡ EXPRESS' : '📦 STANDARD'}
                    </span>
                  </div>
                  <div className="sdm-route-row">
                    <span>Distance:</span>
                    <span className="sdm-route-value">{routes[currentRouteIndex].distance} mi</span>
                  </div>
                  <div className="sdm-route-row">
                    <span>Est. Time:</span>
                    <span className="sdm-route-value">{routes[currentRouteIndex].estimatedTime} min</span>
                  </div>
                  
                  <div className="sdm-route-progress-bar">
                    <div className="sdm-route-progress-header">
                      <span>Route Progress</span>
                      <span>
                        {routes[currentRouteIndex].homes.filter(h => deliveredHomes.has(h.id)).length} / {routes[currentRouteIndex].homes.length}
                      </span>
                    </div>
                    <div className="sdm-progress-bar">
                      <div
                        className="sdm-progress-fill"
                        style={{
                          width: `${(routes[currentRouteIndex].homes.filter(h => deliveredHomes.has(h.id)).length / routes[currentRouteIndex].homes.length) * 100}%`
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="sdm-route-complete">
                  <div className="sdm-complete-icon">🎉</div>
                  <div className="sdm-complete-title">All Routes Complete!</div>
                  <div className="sdm-complete-subtitle">
                    {deliveredHomes.size} deliveries completed
                  </div>
                </div>
              )}
            </div>

            {/* Performance Metrics */}
            <div className="sdm-performance">
              <h3 className="sdm-panel-title">
                <Target className="sdm-panel-icon" />
                Performance
              </h3>
              <div className="sdm-performance-grid">
                <div className="sdm-performance-item">
                  <div className="sdm-performance-value cyan">{efficiency}</div>
                  <div className="sdm-performance-label">Deliveries/Hour</div>
                </div>
                <div className="sdm-performance-item">
                  <div className="sdm-performance-value green">
                    {Math.round((deliveredPackages / totalPackages) * 100)}%
                  </div>
                  <div className="sdm-performance-label">Package Success</div>
                </div>
                <div className="sdm-performance-item">
                  <div className="sdm-performance-value orange">
                    {startTimeRef.current ? Math.round((Date.now() - startTimeRef.current) / 60000) : 0}
                  </div>
                  <div className="sdm-performance-label">Minutes Elapsed</div>
                </div>
                <div className="sdm-performance-item">
                  <div className="sdm-performance-value pink">
                    {routes.reduce((sum, route) => sum + parseFloat(route.distance), 0).toFixed(1)}
                  </div>
                  <div className="sdm-performance-label">Total Miles</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Bottom Progress Bar */}
        <div className="sdm-progress">
          <div className="sdm-progress-header">
            <div className="sdm-progress-left">
              <h3 className="sdm-progress-title">
                <Truck className="sdm-progress-icon" />
                Fleet Operations Dashboard
              </h3>
              <div className="sdm-progress-subtitle">
                San Diego County • Zone SCF-921 • {currentTime.toLocaleDateString()}
              </div>
            </div>
            <div className="sdm-progress-right">
              <div className="sdm-progress-num">
                {Math.round((deliveredHomes.size / homes.length) * 100)}%
              </div>
              <div className="sdm-progress-sub">
                {deliveredHomes.size} of {homes.length} locations • {deliveredPackages} of {totalPackages} packages
              </div>
            </div>
          </div>
          
          <div className="sdm-progress-bar">
            <div
              className="sdm-progress-fill"
              style={{ 
                width: `${(deliveredHomes.size / homes.length) * 100}%`
              }}
            >
              <div className="sdm-progress-shine"></div>
            </div>
            
            {/* Route markers */}
            {routes.map((_, idx) => (
              <div
                key={idx}
                className={`sdm-route-marker ${idx <= currentRouteIndex ? 'completed' : ''}`}
                style={{ left: `${((idx + 1) / routes.length) * 100}%` }}
              />
            ))}
          </div>
          
          <div className="sdm-progress-footer">
            <div className="sdm-progress-meta">
              <span>Started: {startTimeRef.current ? new Date(startTimeRef.current).toLocaleTimeString() : '--:--'}</span>
              <span className="sdm-status">
                <div className="sdm-status-dot"></div>
                {isAnimating ? 'ACTIVE' : 'STANDBY'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SanDiegoMailDeliveryMap;